import { useLazyQuery, useMutation } from "@apollo/client";
import { Box, Grid, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { CheckboxField } from "components/Forms/CheckboxField";
import { DateField } from "components/Forms/DateField";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { IssueListButton } from "components/Forms/IssueListButton";
import { safeParseFloat } from "encamp-shared/src/tierii/utilities";
import {
  GetWasteContainerQuery,
  Issue,
  IssueLevel,
  SetContainerAmountInput,
  UnitType,
} from "generated-graphql/graphql";
import { useValidatingForm } from "hooks/useValidatingForm";
import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { toUtcMidnightIsoFromDateLike } from "util/dates";
import { hasCriticalIssues } from "util/forms";

import {
  CONTAINER_MUTATION_REFETCH_QUERIES,
  SET_CONTAINER_AMOUNT_MUTATION,
  VALIDATE_SET_CONTAINER_AMOUNT_INPUT,
} from "./api";
import {
  formatQuantity,
  isLocalDateInFuture,
  OCCURRED_AT_SHORTCUTS,
  UNIT_SELECT_ITEMS,
} from "./containerDisplay";

type WasteContainerDetail = NonNullable<GetWasteContainerQuery["wasteContainer"]>;

type SetAmountFormValues = {
  amount: string;
  unit: UnitType | null;
  occurredAt: string | null;
  isEstimate: boolean;
};

function localIssue(key: string, message: string): Issue {
  return {
    level: IssueLevel.Critical,
    key,
    message,
    modelName: "WasteContainer",
    dataName: null,
    jurisdictions: [],
  };
}

function toSetAmountInput(
  values: SetAmountFormValues,
  containerId: string
): SetContainerAmountInput | null {
  const amount = safeParseFloat(values.amount);
  if (amount === null || !values.unit || !values.occurredAt) {
    return null;
  }
  const occurredAt = toUtcMidnightIsoFromDateLike(DateTime.fromISO(values.occurredAt));
  if (!occurredAt) {
    return null;
  }
  return {
    containerId,
    amount,
    unit: values.unit,
    isEstimate: values.isEstimate,
    occurredAt,
  };
}

function incompleteFormIssues(values: SetAmountFormValues): Issue[] {
  const issues: Issue[] = [];
  if (safeParseFloat(values.amount) === null) {
    issues.push(localIssue("amount", "Enter the new total"));
  }
  if (!values.unit) {
    issues.push(localIssue("unit", "Select a unit"));
  }
  if (!values.occurredAt) {
    issues.push(localIssue("occurredAt", "Enter the date of this total"));
  }
  return issues;
}

export type SetContainerAmountDialogProps = {
  container: WasteContainerDetail;
  onClose: () => void;
};

export function SetContainerAmountDialog({ container, onClose }: SetContainerAmountDialogProps) {
  const alerts = useAlerts();

  const defaultValues = useMemo<SetAmountFormValues>(
    () => ({
      amount: container.amount != null ? String(container.amount) : "",
      unit: container.amountUnit ?? container.capacityUnit ?? null,
      occurredAt: DateTime.local().toISODate(),
      isEstimate: container.isEstimate,
    }),
    [container]
  );

  const [validateQuery] = useLazyQuery(VALIDATE_SET_CONTAINER_AMOUNT_INPUT, {
    fetchPolicy: "no-cache",
  });
  const validateInput = useCallback(
    async (values: SetAmountFormValues): Promise<Issue[]> => {
      const input = toSetAmountInput(values, container.id);
      if (!input) {
        return incompleteFormIssues(values);
      }
      // Timezone-aware future-date guard on the complete form (the server only
      // sees a UTC-midnight value and can't do this precisely).
      if (isLocalDateInFuture(values.occurredAt)) {
        return [localIssue("occurredAt", "The date can't be in the future")];
      }
      const { data } = await validateQuery({ variables: { input } });
      return data?.setContainerAmountInputValidator ?? [];
    },
    [validateQuery, container.id]
  );

  const form = useValidatingForm<SetAmountFormValues>(
    defaultValues,
    // The container's standing issues (over capacity, below-capacity FULL),
    // from the query that already loaded it, so they show on open.
    container.issues,
    validateInput,
    undefined,
    {
      // A background refetch mid-edit re-issues the defaults; keep what the
      // user has typed rather than resetting under their cursor.
      keepDirtyValuesOnReset: true,
    }
  );
  const { control, getValues, issues } = form;

  const [setAmount, { loading: isSaving }] = useMutation(SET_CONTAINER_AMOUNT_MUTATION, {
    refetchQueries: CONTAINER_MUTATION_REFETCH_QUERIES,
  });

  const onSubmit = async () => {
    const values = getValues();
    const input = toSetAmountInput(values, container.id);
    if (!input) {
      alerts.error("Enter the new total, its unit, and the date");
      return;
    }
    const submitIssues = await validateInput(values);
    if (hasCriticalIssues(submitIssues)) {
      const firstCritical = submitIssues.find((issue) => issue.level === IssueLevel.Critical);
      alerts.error(firstCritical?.message ?? "Fix the critical issues before saving");
      return;
    }
    await setAmount({
      variables: { input },
      onCompleted: () => {
        alerts.success("Container amount updated");
        onClose();
      },
      onError: (error) => {
        alerts.error(error.message ?? "An error occurred while setting the amount");
      },
    });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xs"
      title="Set amount"
      primaryActionButtonProps={{
        loading: isSaving,
        onClick: onSubmit,
        // No dirty gate: saving the prefilled total unchanged is the
        // "verified the count" flow and writes its own zero-delta record.
        disabled: hasCriticalIssues(issues),
      }}
      additionalPrimaryActionElements={[<IssueListButton key="issues" issues={issues} />]}
    >
      <SetContainerAmountFields form={form} container={container} />
    </Dialog>
  );
}

/**
 * The new total, its unit, when it was counted, and whether it's an estimate.
 * Split from the dialog shell, which owns the mutation and the save gate.
 */
function SetContainerAmountFields({
  form,
  container,
}: {
  form: ReturnType<typeof useValidatingForm<SetAmountFormValues>>;
  container: WasteContainerDetail;
}) {
  return (
    <FormProvider {...form}>
      <Box component="form" sx={{ pt: 1 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" color="text.secondary">
              Current recorded total: {formatQuantity(container.amount, container.amountUnit)}. The
              difference is recorded as a fill event so the history stays complete.
            </Typography>
          </Grid>
          <Grid size={{ xs: 7 }}>
            <FormTextField
              name="amount"
              label="New total"
              control={form.control}
              type="number"
              textFieldProps={{ required: true }}
            />
          </Grid>
          <Grid size={{ xs: 5 }}>
            <FormSelect
              name="unit"
              label="Unit"
              control={form.control}
              selectItems={UNIT_SELECT_ITEMS}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <DateField
              name="occurredAt"
              label="Date"
              control={form.control}
              required
              shortcuts={OCCURRED_AT_SHORTCUTS}
              datePickerProps={{ disableFuture: true }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <CheckboxField
              name="isEstimate"
              label="This amount is an estimate"
              control={form.control}
              noErrorDisplay
            />
          </Grid>
        </Grid>
      </Box>
    </FormProvider>
  );
}
